import { Calendar, CalendarClock, CalendarDays, Sparkles, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { useLocation as useRouterLocation } from "react-router-dom";

export const FrequencyDiscounts = () => {
  const { pathname } = useRouterLocation();
  const isEs = pathname.startsWith('/es');
  const isRu = pathname.startsWith('/ru');
  const isZh = pathname.startsWith('/zh');

  const t = (en: string, es: string, ru: string, zh: string) =>
    isEs ? es : isRu ? ru : isZh ? zh : en;

  const frequencies = [
    {
      id: "weekly",
      title: t("Weekly", "Semanal", "Еженедельно", "每周"),
      discount: t("35% OFF", "35% DE DESCUENTO", "СКИДКА 35%", "优惠35%"),
      description: t(
        "Perfect for busy families who want a consistently spotless home.",
        "Perfecto para familias ocupadas que quieren un hogar consistentemente impecable.",
        "Идеально для занятых семей, которые хотят безупречно чистый дом.",
        "非常适合希望家居保持洁净的忙碌家庭。"
      ),
      icon: Calendar,
      popular: false,
      bestValue: true,
    },
    {
      title: t("Bi-Weekly", "Quincenal", "Раз в две недели", "每两周"),
      discount: t("30% OFF", "30% DE DESCUENTO", "СКИДКА 30%", "优惠30%"),
      description: t(
        "Our most popular option. Great for maintaining a clean, healthy space.",
        "Nuestra opción más popular. Genial para mantener un espacio limpio y saludable.",
        "Самый популярный вариант. Поддерживайте чистоту и здоровую атмосферу.",
        "最受欢迎的选择。保持清洁健康的生活空间。"
      ),
      icon: CalendarDays,
      popular: true,
    },
    {
      title: t("Monthly", "Mensual", "Ежемесячно", "每月"),
      discount: t("20% OFF", "20% DE DESCUENTO", "СКИДКА 20%", "优惠20%"),
      description: t(
        "Ideal for thorough touch-ups and keeping dust and dirt at bay.",
        "Ideal para retoques profundos y mantener el polvo y la suciedad a raya.",
        "Идеально для основательной уборки и поддержания чистоты.",
        "适合定期深度清洁，保持家居整洁无尘。"
      ),
      icon: CalendarClock,
      popular: false,
    },
    {
      title: t("One-Time", "Una Vez", "Разовая", "单次"),
      discount: t("Standard", "Estándar", "Стандарт", "标准"),
      description: t(
        "Great for move-in/out, spring cleaning, or preparing for an event.",
        "Genial para mudanzas, limpieza de primavera o preparación para un evento.",
        "Отлично для переезда, генеральной уборки или подготовки к мероприятию.",
        "适用于搬家、大扫除或活动前准备。"
      ),
      icon: Sparkles,
      popular: false,
    },
  ];

  const mostPopularLabel = t("Most Popular", "Más Popular", "Самый Popular", "最受欢迎");
  const bestValueLabel = t("Best Value", "Mejor Valor", "Лучшая цена", "最超值");
  const bookLabel = (title: string) => t(`Book ${title}`, `Reservar ${title}`, `Записаться: ${title}`, `预约${title}`);

  return (
    <section className="py-24 bg-muted/30">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            {t(
              "Save More with Recurring Cleans",
              "Ahorra Más con Limpiezas Recurrentes",
              "Экономьте больше при регулярных уборках",
              "定期清洁享更多优惠"
            )}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            {t(
              "Keep your home consistently clean and enjoy exclusive discounts when you book recurring services. No contracts, cancel anytime.",
              "Mantén tu hogar consistentemente limpio y disfruta de descuentos exclusivos al reservar servicios recurrentes. Sin contratos, cancela cuando quieras.",
              "Поддерживайте чистоту и пользуйтесь эксклюзивными скидками при регулярном бронировании. Без контрактов, отмена в любое время.",
              "保持家居持续整洁，享受定期预约专属折扣。无合同约束，随时可取消。"
            )}
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {frequencies.map((freq, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg flex flex-col ${
                  freq.popular ? 'border-primary shadow-md md:scale-105 z-10' : 'hover:-translate-y-1'
                }`}
              >
              {freq.popular && (
                <div className="absolute top-0 left-0 w-full bg-primary text-primary-foreground text-center py-0.5 md:py-1 text-[9px] md:text-xs font-bold uppercase tracking-wider">
                  {mostPopularLabel}
                </div>
              )}
              {freq.bestValue && (
                <div className="absolute top-0 left-0 w-full bg-accent text-accent-foreground text-center py-0.5 md:py-1 text-[9px] md:text-xs font-bold uppercase tracking-wider">
                  ✨ {bestValueLabel} ✨
                </div>
              )}
              <CardHeader className={`text-center p-3 md:p-6 ${(freq.popular || freq.bestValue) ? 'pt-6 md:pt-10' : ''}`}>
                <div className="mx-auto w-8 h-8 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2 md:mb-4 text-primary">
                  <freq.icon className="w-4 h-4 md:w-6 md:h-6" />
                </div>
                <CardTitle className="text-sm md:text-xl mb-1 md:mb-2">{freq.title}</CardTitle>
                <div className="text-lg md:text-3xl font-extrabold text-primary mb-1 md:mb-2">
                  {freq.discount}
                </div>
              </CardHeader>
              <CardContent className="text-center p-3 pt-0 md:p-6 md:pt-0 flex flex-col flex-1">
                <p className="text-muted-foreground text-[10px] md:text-sm mb-3 md:mb-6 flex-1 min-h-[40px] md:min-h-[60px]">
                  {freq.description}
                </p>
                <Button 
                  className="w-full font-semibold text-[10px] md:text-sm h-8 md:h-10 px-2" 
                  variant={freq.popular ? "default" : "outline"}
                  onClick={() => document.dispatchEvent(new Event('open-booking'))}
                >
                  {bookLabel(freq.title)}
                </Button>
              </CardContent>
            </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
